import { useState, useEffect } from 'react';
import { OpsLayout } from '@/components/ops/OpsLayout';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { Calendar, Users, Settings2, List, Plus, Trash2, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface MeetingType {
  id: string; slug: string; title: string; description: string | null;
  duration_minutes: number; buffer_before_minutes: number; buffer_after_minutes: number;
  location_type: string; location_value: string | null; min_notice_hours: number;
  booking_window_days: number; max_bookings_per_day: number | null; is_active: boolean;
}

interface Host {
  id: string; display_name: string; email: string; timezone: string;
  working_hours_json: Record<string, { start: string; end: string } | undefined>;
  is_active: boolean;
}

interface Booking {
  id: string; start_time: string; end_time: string; invitee_first_name: string;
  invitee_last_name: string; invitee_email: string; invitee_org: string | null;
  invitee_role: string | null; status: string; created_at: string;
}

const AdminSchedulerPage = () => {
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [hosts, setHosts] = useState<Host[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  // New host form
  const [newHostName, setNewHostName] = useState('');
  const [newHostEmail, setNewHostEmail] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    const [mtRes, hostRes, bookRes] = await Promise.all([
      supabase.from('scheduler_meeting_types').select('*').order('created_at'),
      supabase.from('scheduler_hosts').select('*').order('created_at'),
      supabase.from('scheduler_bookings').select('*').order('start_time', { ascending: false }).limit(50),
    ]);
    setMeetingTypes((mtRes.data || []) as unknown as MeetingType[]);
    setHosts((hostRes.data || []) as unknown as Host[]);
    setBookings((bookRes.data || []) as unknown as Booking[]);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const toggleMeetingType = async (id: string, active: boolean) => {
    await supabase.from('scheduler_meeting_types').update({ is_active: active }).eq('id', id);
    setMeetingTypes(prev => prev.map(m => m.id === id ? { ...m, is_active: active } : m));
    toast.success(`Meeting type ${active ? 'enabled' : 'disabled'}`);
  };

  const addHost = async () => {
    if (!newHostName || !newHostEmail) return;
    const { error } = await supabase.from('scheduler_hosts').insert({
      display_name: newHostName,
      email: newHostEmail,
    });
    if (error) { toast.error(error.message); return; }
    toast.success('Host added');
    setNewHostName('');
    setNewHostEmail('');
    fetchAll();
  };

  const toggleHost = async (id: string, active: boolean) => {
    await supabase.from('scheduler_hosts').update({ is_active: active }).eq('id', id);
    setHosts(prev => prev.map(h => h.id === id ? { ...h, is_active: active } : h));
  };

  const statusColor = (s: string) => {
    if (s === 'confirmed' || s === 'confirmed_demo') return 'bg-success/10 text-success border-success/20';
    if (s === 'cancelled') return 'bg-destructive/10 text-destructive border-destructive/20';
    return 'bg-warning/10 text-warning border-warning/20';
  };

  return (
    <OpsLayout>
      <div className="w-full max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="heading-2">Scheduler Administration</h1>
          <p className="text-sm text-muted-foreground">Manage meeting types, hosts, and view bookings.</p>
        </div>

        <Tabs defaultValue="bookings">
          <TabsList>
            <TabsTrigger value="bookings" className="gap-1.5"><List className="w-3.5 h-3.5" />Bookings</TabsTrigger>
            <TabsTrigger value="meeting-types" className="gap-1.5"><Calendar className="w-3.5 h-3.5" />Meeting Types</TabsTrigger>
            <TabsTrigger value="hosts" className="gap-1.5"><Users className="w-3.5 h-3.5" />Hosts</TabsTrigger>
            <TabsTrigger value="integrations" className="gap-1.5"><Settings2 className="w-3.5 h-3.5" />Calendar Integrations</TabsTrigger>
          </TabsList>

          {/* Bookings */}
          <TabsContent value="bookings">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">Recent Bookings</CardTitle>
                  <CardDescription>Last 50 bookings</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchAll} className="gap-1">
                  <RefreshCw className="w-3 h-3" /> Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {bookings.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No bookings yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date & Time</TableHead>
                        <TableHead>Invitee</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.map(b => (
                        <TableRow key={b.id}>
                          <TableCell className="text-sm">
                            {format(new Date(b.start_time), 'MMM d, yyyy h:mm a')}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm font-medium">{b.invitee_first_name} {b.invitee_last_name}</div>
                            <div className="text-xs text-muted-foreground">{b.invitee_email}</div>
                          </TableCell>
                          <TableCell className="text-sm">{b.invitee_org || '—'}</TableCell>
                          <TableCell className="text-sm">{b.invitee_role || '—'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={statusColor(b.status)}>
                              {b.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Meeting Types */}
          <TabsContent value="meeting-types">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Meeting Types</CardTitle>
                <CardDescription>Configure available meeting options for prospects.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {meetingTypes.map(mt => (
                    <div key={mt.id} className="flex items-center justify-between p-4 rounded-lg border border-border/50 bg-card/30">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold">{mt.title}</h3>
                          <Badge variant="outline" className="text-[10px]">{mt.slug}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {mt.duration_minutes} min • {mt.buffer_before_minutes}m buffer before • {mt.buffer_after_minutes}m after • {mt.min_notice_hours}h notice
                        </p>
                      </div>
                      <Switch checked={mt.is_active} onCheckedChange={v => toggleMeetingType(mt.id, v)} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hosts */}
          <TabsContent value="hosts">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Hosts</CardTitle>
                <CardDescription>People who can receive bookings.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Display Name</Label>
                    <Input value={newHostName} onChange={e => setNewHostName(e.target.value)} placeholder="Dr. Larkin" className="mt-1" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Email</Label>
                    <Input value={newHostEmail} onChange={e => setNewHostEmail(e.target.value)} placeholder="larkin@linkmedicalai.com" className="mt-1" />
                  </div>
                  <Button size="sm" onClick={addHost} className="gap-1 bg-success hover:bg-success/90 text-success-foreground">
                    <Plus className="w-3 h-3" /> Add
                  </Button>
                </div>

                {hosts.map(h => (
                  <div key={h.id} className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-card/30">
                    <div>
                      <p className="text-sm font-medium">{h.display_name}</p>
                      <p className="text-xs text-muted-foreground">{h.email} • {h.timezone}</p>
                    </div>
                    <Switch checked={h.is_active} onCheckedChange={v => toggleHost(h.id, v)} />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Integrations */}
          <TabsContent value="integrations">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Calendar Integrations</CardTitle>
                <CardDescription>Connect host calendars to check availability and create events.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-6 rounded-xl border border-border/30 bg-card/20 text-center">
                  <Settings2 className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                  <h3 className="text-sm font-semibold text-foreground mb-1">Calendar Integration Setup</h3>
                  <p className="text-xs text-muted-foreground mb-4 max-w-md mx-auto">
                    Calendar integration requires configuring API credentials for your calendar provider 
                    (Google Calendar OAuth or iCloud CalDAV). Contact engineering to configure:
                  </p>
                  <div className="text-left max-w-sm mx-auto space-y-2 text-xs text-muted-foreground">
                    <p><strong className="text-foreground">Google Calendar:</strong> Set GOOGLE_OAUTH_CLIENT_ID and GOOGLE_OAUTH_SECRET as backend secrets.</p>
                    <p><strong className="text-foreground">Apple/iCloud:</strong> Use a provider like Nylas or Cronofy that supports CalDAV, or set up direct CalDAV with an app-specific password.</p>
                    <p><strong className="text-foreground">Provider keys:</strong> CAL_PROVIDER, NYLAS_API_KEY or CRONOFY_CLIENT_ID/SECRET, and ENCRYPTION_KEY.</p>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-4">
                    Until calendar integration is configured, the scheduler operates using database-only availability 
                    (working hours minus existing bookings).
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </OpsLayout>
  );
};

export default AdminSchedulerPage;
